/**
 * DiffViewer — side-by-side rendering of the existing diff API
 * `GET /api/careplans/{plan_id}/diff/{previous_id}`. Tolerates the
 * existing diff response shape (a mapping of `field -> {before, after}`
 * for top-level legacy fields). Structured-row diffs are presented as
 * a flat "added / removed / modified" partition based on the row id
 * across the two structured envelopes.
 */
import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, GitCompare } from 'lucide-react';

function listRowIds(structured) {
  const out = new Map();  // id -> {section_key, row}
  for (const s of (structured?.sections || [])) {
    for (const r of (s.rows || [])) {
      out.set(r.id, { section_key: s.section_key, row: r });
    }
  }
  return out;
}

export function DiffViewer({ planId, previousId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get(`/careplans/${planId}/diff/${previousId}`),
      api.get(`/careplans/${planId}/structured`),
      api.get(`/careplans/${previousId}/structured`),
    ])
      .then(([d, curr, prev]) => {
        setData({
          legacyDiff: d.data?.data || d.data || {},
          current: curr.data?.data?.structured,
          previous: prev.data?.data?.structured,
        });
      })
      .catch(() => setData({}))
      .finally(() => setLoading(false));
  }, [planId, previousId]);

  if (loading) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
           data-testid="structured-diff-viewer">
        <Card className="w-[400px]">
          <CardContent className="p-6 flex items-center gap-2 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Computing diff…
          </CardContent>
        </Card>
      </div>
    );
  }

  const curr = listRowIds(data?.current);
  const prev = listRowIds(data?.previous);
  const added = [...curr.keys()].filter((k) => !prev.has(k));
  const removed = [...prev.keys()].filter((k) => !curr.has(k));
  const modified = [...curr.keys()].filter((k) => prev.has(k)
    && JSON.stringify(curr.get(k).row) !== JSON.stringify(prev.get(k).row));

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
         data-testid="structured-diff-viewer">
      <Card className="w-[860px] max-w-full max-h-[88vh] overflow-y-auto">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2">
              <GitCompare className="h-4 w-4" /> Diff — current vs v{previousId}
            </h3>
            <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
          </div>

          <div className="grid grid-cols-3 gap-2 text-sm">
            <div className="border rounded p-2">
              <div className="text-xs font-semibold text-emerald-700">Added rows ({added.length})</div>
              <ul className="text-xs space-y-1 mt-1">
                {added.map((id) => (
                  <li key={id} data-testid={`diff-added-${id}`}>
                    <span className="font-mono">{id}</span>
                    <span className="opacity-60 ml-1">@ {curr.get(id)?.section_key}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="border rounded p-2">
              <div className="text-xs font-semibold text-rose-700">Removed rows ({removed.length})</div>
              <ul className="text-xs space-y-1 mt-1">
                {removed.map((id) => (
                  <li key={id} data-testid={`diff-removed-${id}`}>
                    <span className="font-mono">{id}</span>
                    <span className="opacity-60 ml-1">@ {prev.get(id)?.section_key}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="border rounded p-2">
              <div className="text-xs font-semibold text-amber-700">Modified rows ({modified.length})</div>
              <ul className="text-xs space-y-1 mt-1">
                {modified.map((id) => (
                  <li key={id} data-testid={`diff-modified-${id}`}>
                    <span className="font-mono">{id}</span>
                    <span className="opacity-60 ml-1">@ {curr.get(id)?.section_key}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {Object.keys(data?.legacyDiff || {}).length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Legacy field changes
              </div>
              <table className="w-full mt-1 text-xs">
                <thead>
                  <tr className="text-left text-muted-foreground border-b">
                    <th className="py-1 pr-2">Field</th>
                    <th className="py-1 pr-2">Before</th>
                    <th className="py-1">After</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(data.legacyDiff).map(([field, change]) => (
                    <tr key={field} className="align-top border-b last:border-none" data-testid={`diff-legacy-${field}`}>
                      <td className="py-1 pr-2 font-mono whitespace-nowrap">{field}</td>
                      <td className="py-1 pr-2 text-rose-700 line-through">{String(change?.before ?? '')}</td>
                      <td className="py-1 text-emerald-700">{String(change?.after ?? '')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default DiffViewer;
